// ==========================================================
// LINE LIFF & QR Code Authentication Utility
// ==========================================================
import { supabase } from './supabaseClient';

export const LIFF_ID = '2011673157-IavFBbpv';
export const LINE_CHANNEL_ID = '2011673157';

let liffLoadingPromise = null;
let liffInitPromise = null;

/**
 * Dynamically loads the LIFF SDK from LINE CDN.
 * Avoids any React 19 npm peerDependency issues.
 */
export const loadLiff = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  // If liff is already loaded globally
  if (window.liff) {
    return Promise.resolve(window.liff);
  }

  // In test environment without mock, resolve null immediately to prevent hanging
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test' && !window.liff) {
    return Promise.resolve(null);
  }

  if (liffLoadingPromise) {
    return liffLoadingPromise;
  }

  liffLoadingPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[src*="liff/edge/2/sdk.js"]');
    if (existingScript) {
      if (window.liff) {
        return resolve(window.liff);
      }
      existingScript.addEventListener('load', () => resolve(window.liff));
      existingScript.addEventListener('error', (err) => reject(new Error('Failed to load LIFF SDK script')));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://static.line-scdn.net/liff/edge/2/sdk.js';
    script.async = true;
    script.onload = () => {
      resolve(window.liff);
    };
    script.onerror = () => {
      liffLoadingPromise = null;
      reject(new Error('Failed to load LINE LIFF SDK from CDN'));
    };
    document.body.appendChild(script);
  });

  return liffLoadingPromise;
};

/**
 * Initializes the LIFF SDK with the configured LIFF ID.
 */
export const initLiff = async () => {
  if (typeof window === 'undefined') return null;

  if (liffInitPromise) {
    return liffInitPromise;
  }

  liffInitPromise = (async () => {
    try {
      const liff = await loadLiff();
      if (!liff) return null;

      // Handle cases where LIFF is already initialized
      try {
        await liff.init({ liffId: LIFF_ID });
      } catch (err) {
        const msg = err?.message || '';
        if (!msg.includes('already initialized') && !msg.includes('init() has already been called')) {
          throw err;
        }
      }
      return liff;
    } catch (err) {
      liffInitPromise = null;
      console.error('LIFF initialization failed:', err);
      throw err;
    }
  })();

  return liffInitPromise;
};

/**
 * Triggers LINE Login.
 * On desktop browsers, LINE automatically presents the QR Code login screen!
 */
export const loginWithLine = (options = {}) => {
  if (typeof window !== 'undefined' && window.liff && typeof window.liff.login === 'function') {
    const redirectUri = options.redirectUri || (typeof window !== 'undefined' ? window.location.origin : undefined);
    window.liff.login({ redirectUri, ...options });
  } else {
    console.warn('LIFF SDK not ready for login');
  }
};

/**
 * Logs out of the current LINE session.
 */
export const logoutLine = () => {
  try {
    if (typeof window !== 'undefined' && window.liff && typeof window.liff.logout === 'function') {
      if (typeof window.liff.isLoggedIn === 'function' && window.liff.isLoggedIn()) {
        window.liff.logout();
      }
    }
  } catch (err) {
    console.error('Error logging out from LINE:', err);
  }
};

/**
 * Gets the current LINE profile if logged in.
 */
export const getLineProfile = async () => {
  try {
    if (typeof window !== 'undefined' && window.liff && typeof window.liff.isLoggedIn === 'function') {
      if (window.liff.isLoggedIn()) {
        const profile = await window.liff.getProfile();
        return profile;
      }
    }
    return null;
  } catch (err) {
    console.error('Error fetching LINE profile:', err);
    return null;
  }
};

/**
 * Helper to check if user is logged into LINE.
 */
export const isLineLoggedIn = () => {
  if (typeof window !== 'undefined' && window.liff && typeof window.liff.isLoggedIn === 'function') {
    return Boolean(window.liff.isLoggedIn());
  }
  return false;
};

/**
 * Broadcasts 'login-approved' event via Supabase Realtime channel.
 */
export const broadcastLoginApproved = async (token, userData) => {
  if (!token) return;
  try {
    if (supabase && typeof supabase.channel === 'function') {
      const channel = supabase.channel(`line-login-${token}`);
      await channel.subscribe();
      await channel.send({
        type: 'broadcast',
        event: 'login-approved',
        payload: userData || {}
      });
    }
  } catch (err) {
    console.error('Failed to broadcast login approval:', err);
  }
};

/**
 * Subscribes to login token approval via Supabase Realtime + fallback polling.
 * Returns an unsubscribe cleanup function.
 */
export const subscribeToLoginToken = (token, onApprovedCallback) => {
  if (!token) return () => {};

  let isCleanedUp = false;
  let pollInterval = null;
  let channel = null;

  // 1. Supabase Realtime Broadcast Subscription
  if (supabase && typeof supabase.channel === 'function') {
    try {
      channel = supabase.channel(`line-login-${token}`);
      channel
        .on('broadcast', { event: 'login-approved' }, (payload) => {
          if (isCleanedUp) return;
          if (onApprovedCallback) {
            onApprovedCallback(payload?.payload || payload);
          }
        })
        .subscribe();
    } catch (err) {
      console.warn('Realtime channel subscription error:', err);
    }
  }

  // 2. Fallback Polling every 2 seconds via check_login_token_status RPC
  if (supabase && typeof supabase.rpc === 'function') {
    pollInterval = setInterval(async () => {
      if (isCleanedUp) return;
      try {
        const { data, error } = await supabase.rpc('check_login_token_status', { p_token: token });
        if (!error && Array.isArray(data) && data.length > 0) {
          const row = data[0];
          if (row.status === 'approved') {
            if (isCleanedUp) return;
            isCleanedUp = true;
            clearInterval(pollInterval);
            if (channel && typeof channel.unsubscribe === 'function') {
              try { channel.unsubscribe(); } catch (e) {}
            }
            if (onApprovedCallback) {
              onApprovedCallback(row);
            }
          }
        }
      } catch (e) {
        console.warn('Error during token status poll:', e);
      }
    }, 2000);
  }

  return () => {
    isCleanedUp = true;
    if (pollInterval) {
      clearInterval(pollInterval);
    }
    if (channel && typeof channel.unsubscribe === 'function') {
      try { channel.unsubscribe(); } catch (e) {}
    }
  };
};

/**
 * Broadcasts 'binding-approved' event via Supabase Realtime channel.
 */
export const broadcastBindingApproved = async (token, lineData) => {
  if (!token) return;
  try {
    if (supabase && typeof supabase.channel === 'function') {
      const channel = supabase.channel(`line-binding-${token}`);
      await channel.subscribe();
      await channel.send({
        type: 'broadcast',
        event: 'binding-approved',
        payload: lineData || {}
      });
    }
  } catch (err) {
    console.error('Failed to broadcast binding approval:', err);
  }
};

/**
 * Subscribes to binding token approval via Supabase Realtime + fallback polling.
 * Returns an unsubscribe cleanup function.
 */
export const subscribeToBindingToken = (token, onApprovedCallback) => {
  if (!token) return () => {};

  let isCleanedUp = false;
  let pollInterval = null;
  let channel = null;

  // 1. Supabase Realtime Broadcast Subscription
  if (supabase && typeof supabase.channel === 'function') {
    try {
      channel = supabase.channel(`line-binding-${token}`);
      channel
        .on('broadcast', { event: 'binding-approved' }, (payload) => {
          if (isCleanedUp) return;
          if (onApprovedCallback) {
            onApprovedCallback(payload?.payload || payload);
          }
        })
        .subscribe();
    } catch (err) {
      console.warn('Realtime binding channel subscription error:', err);
    }
  }

  // 2. Fallback Polling every 2 seconds via check_binding_status RPC
  if (supabase && typeof supabase.rpc === 'function') {
    pollInterval = setInterval(async () => {
      if (isCleanedUp) return;
      try {
        const { data, error } = await supabase.rpc('check_binding_status', { p_token: token });
        if (!error && Array.isArray(data) && data.length > 0) {
          const row = data[0];
          if (row.status === 'approved') {
            if (isCleanedUp) return;
            isCleanedUp = true;
            clearInterval(pollInterval);
            if (channel && typeof channel.unsubscribe === 'function') {
              try { channel.unsubscribe(); } catch (e) {}
            }
            if (onApprovedCallback) {
              onApprovedCallback(row);
            }
          }
        }
      } catch (e) {
        console.warn('Error during binding token status poll:', e);
      }
    }, 2000);
  }

  return () => {
    isCleanedUp = true;
    if (pollInterval) {
      clearInterval(pollInterval);
    }
    if (channel && typeof channel.unsubscribe === 'function') {
      try { channel.unsubscribe(); } catch (e) {}
    }
  };
};
